import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DestinationInput } from "@/components/DestinationInput";
import { TransferLocationInput } from "@/components/TransferLocationInput";
import { Hotel, Plane, Ticket, Car, Search, Sparkles } from "lucide-react";

const todayPlus = (d) => { const t = new Date(); t.setDate(t.getDate() + d); return t.toISOString().slice(0, 10); };
const inputCls = "w-full h-12 rounded-xl border border-[#E2E8F0] bg-white px-3.5 text-sm font-semibold text-[#0B2038] focus:outline-none focus:ring-2 focus:ring-[#009E9E]/40 focus:border-[#009E9E] transition-colors";

const Field = ({ label, children, className = "" }) => (
  <div className={`min-w-0 ${className}`}>
    <label className="block text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#475569] mb-1.5">{label}</label>
    {children}
  </div>
);

const GoButton = ({ testid, onClick, label, disabled }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className="h-12 w-full lg:w-auto px-7 rounded-xl bg-[#009E9E] hover:bg-[#007A7A] disabled:bg-[#94A3B8] disabled:cursor-not-allowed text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shrink-0">
    <Search className="w-4 h-4" strokeWidth={2.4} />{label}
  </button>
);

export const SearchWidget = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [dests, setDests] = useState([]);
  const [aiQuery, setAiQuery] = useState("");
  const [hotel, setHotel] = useState({ destination: "IST", checkIn: todayPlus(21), checkOut: todayPlus(24), adults: 2, children: 0, rooms: 1 });
  const [flight, setFlight] = useState({ origin: "AYT", destination: "IST", departDate: todayPlus(21), returnDate: "", adults: 1 });
  const [activity, setActivity] = useState({ destination: "IST", fromDate: todayPlus(21), toDate: todayPlus(28) });
  const [transfer, setTransfer] = useState({ airportId: "ayt", locationId: "", locationName: "", direction: "arrival", date: todayPlus(21), time: "10:00", adults: 2 });

  useEffect(() => { api.get("/destinations").then((r) => setDests(r.data)).catch(() => {}); }, []);
  const q = (obj) => new URLSearchParams(obj).toString();
  const runAi = () => { if (aiQuery.trim()) navigate(`/results/hotels?ai=${encodeURIComponent(aiQuery.trim())}`); };

  const destSelect = (value, onChange, testid) => (
    <DestinationInput dests={dests} value={value} onChange={onChange} testid={testid} placeholder={t("dest_placeholder")} />
  );

  const tabs = [["hotels", t("nav_hotels"), Hotel], ["transfers", t("nav_transfers"), Car], ["activities", t("nav_activities"), Ticket], ["flights", t("nav_flights"), Plane]];

  return (
    <div className="w-full bg-white rounded-[26px] card-soft border border-[#E2E8F0] overflow-hidden">
      <Tabs defaultValue="hotels">
        <TabsList className="bg-[#F8FAFC] border-b border-[#E2E8F0] gap-1 p-2 h-auto w-full flex-wrap justify-start rounded-none">
          {tabs.map(([val, lbl, Icon]) => (
            <TabsTrigger key={val} value={val} data-testid={`tab-${val}`}
              className="rounded-xl px-4 py-2.5 text-sm font-bold data-[state=active]:bg-[#0B2038] data-[state=active]:text-white data-[state=active]:shadow-none text-[#475569] transition-colors">
              <Icon className="w-4 h-4 mr-2" strokeWidth={2} /> {lbl}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="p-4 sm:p-5">
          <TabsContent value="hotels" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_.8fr_.8fr_auto] gap-3 items-end">
              <Field label={t("destination")} className="col-span-2 lg:col-span-1">{destSelect(hotel.destination, (v) => setHotel({ ...hotel, destination: v }), "hotel-destination")}</Field>
              <Field label={t("checkin")}><input data-testid="hotel-checkin" type="date" className={inputCls} value={hotel.checkIn} onChange={(e) => setHotel({ ...hotel, checkIn: e.target.value })} /></Field>
              <Field label={t("checkout")}><input data-testid="hotel-checkout" type="date" className={inputCls} value={hotel.checkOut} onChange={(e) => setHotel({ ...hotel, checkOut: e.target.value })} /></Field>
              <Field label={t("guests")}><input data-testid="hotel-adults" type="number" min={1} className={inputCls} value={hotel.adults} onChange={(e) => setHotel({ ...hotel, adults: +e.target.value })} /></Field>
              <Field label={t("rooms")}><input data-testid="hotel-rooms" type="number" min={1} className={inputCls} value={hotel.rooms} onChange={(e) => setHotel({ ...hotel, rooms: +e.target.value })} /></Field>
              <div className="col-span-2 lg:col-span-1"><GoButton testid="hotel-search-button" disabled={!hotel.destination} label={t("search")} onClick={() => navigate(`/results/hotels?${q(hotel)}`)} /></div>
            </div>
          </TabsContent>

          <TabsContent value="transfers" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-[1fr_1.4fr_1fr_.9fr_.7fr_auto] gap-3 items-end">
              <Field label={t("pickup_airport")}>
                <Select value={transfer.airportId} onValueChange={(v) => setTransfer({ ...transfer, airportId: v })}>
                  <SelectTrigger data-testid="transfer-airport" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-[600]">
                    <SelectItem value="ayt">Antalya (AYT)</SelectItem>
                    <SelectItem value="gzp">Gazipaşa-Alanya (GZP)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("dropoff")} className="col-span-2 lg:col-span-1">
                <TransferLocationInput value={transfer.locationId} name={transfer.locationName}
                  onChange={(id, nm) => setTransfer({ ...transfer, locationId: id, locationName: nm })}
                  testid="transfer-location" placeholder={t("dropoff_placeholder")} />
              </Field>
              <Field label={t("direction")}>
                <Select value={transfer.direction} onValueChange={(v) => setTransfer({ ...transfer, direction: v })}>
                  <SelectTrigger data-testid="transfer-direction" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white z-[600]"><SelectItem value="arrival">{t("airport_hotel")}</SelectItem><SelectItem value="departure">{t("hotel_airport")}</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label={t("date")}><input data-testid="transfer-date" type="date" className={inputCls} value={transfer.date} onChange={(e) => setTransfer({ ...transfer, date: e.target.value })} /></Field>
              <Field label={t("guests")}><input data-testid="transfer-adults" type="number" min={1} className={inputCls} value={transfer.adults} onChange={(e) => setTransfer({ ...transfer, adults: +e.target.value })} /></Field>
              <div className="col-span-2 lg:col-span-1"><GoButton testid="transfer-search-button" disabled={!transfer.locationId} label={t("search")} onClick={() => navigate(`/results/transfers?${q({ destination: transfer.airportId.toUpperCase(), airportId: transfer.airportId, locationId: transfer.locationId, locationName: transfer.locationName, direction: transfer.direction, date: transfer.date, time: transfer.time, adults: transfer.adults })}`)} /></div>
            </div>
          </TabsContent>

          <TabsContent value="activities" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_auto] gap-3 items-end">
              <Field label={t("destination")}>{destSelect(activity.destination, (v) => setActivity({ ...activity, destination: v }), "activity-destination")}</Field>
              <Field label={t("from_date")}><input data-testid="activity-from" type="date" className={inputCls} value={activity.fromDate} onChange={(e) => setActivity({ ...activity, fromDate: e.target.value })} /></Field>
              <Field label={t("to_date")}><input data-testid="activity-to" type="date" className={inputCls} value={activity.toDate} onChange={(e) => setActivity({ ...activity, toDate: e.target.value })} /></Field>
              <div className="col-span-2 lg:col-span-1"><GoButton testid="activity-search-button" disabled={!activity.destination} label={t("search")} onClick={() => navigate(`/results/activities?${q(activity)}`)} /></div>
            </div>
          </TabsContent>

          <TabsContent value="flights" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-[1.2fr_1.2fr_1fr_.8fr_auto] gap-3 items-end">
              <Field label={t("from_label")}>{destSelect(flight.origin, (v) => setFlight({ ...flight, origin: v }), "flight-origin")}</Field>
              <Field label={t("to_label")}>{destSelect(flight.destination, (v) => setFlight({ ...flight, destination: v }), "flight-destination")}</Field>
              <Field label={t("date")}><input data-testid="flight-depart" type="date" className={inputCls} value={flight.departDate} onChange={(e) => setFlight({ ...flight, departDate: e.target.value })} /></Field>
              <Field label={t("guests")}><input data-testid="flight-adults" type="number" min={1} className={inputCls} value={flight.adults} onChange={(e) => setFlight({ ...flight, adults: +e.target.value })} /></Field>
              <div className="col-span-2 lg:col-span-1"><GoButton testid="flight-search-button" disabled={!flight.origin || !flight.destination} label={t("search")} onClick={() => navigate(`/results/flights?${q(flight)}`)} /></div>
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* AI prompt strip */}
      <div className="border-t border-[#E2E8F0] bg-[#0B2038] px-4 sm:px-5 py-3 flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-[#C9A227] shrink-0" />
        <input data-testid="ai-search-input" value={aiQuery} onChange={(e) => setAiQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runAi()}
          placeholder={t("ai_placeholder")}
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/50 min-w-0" />
        <button data-testid="ai-search-button" onClick={runAi}
          className="rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider h-9 px-4 shrink-0 transition-colors">
          {t("ai_search")}
        </button>
      </div>
    </div>
  );
};
